import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { homeRoute } from '@/lib/tier';

/**
 * Where Kinde returns the browser after authenticating.
 *
 * The SDK does the actual work — it reads the authorization code out of the query string,
 * exchanges it for tokens using the PKCE verifier it stashed before redirecting, and clears
 * the URL. This component only waits for that to finish and then gets out of the way.
 *
 * ## Why this route exists at all
 *
 * It would be possible to redirect back to `/` and let the SDK pick the code out of the URL
 * there. That is worse for one specific reason: `/` immediately redirects to the tier's home
 * route, and a redirect that fires while the code exchange is still in flight can drop the
 * `?code=` before the SDK reads it. The user lands signed out, with no error, on the screen
 * they were trying to reach. A dedicated route that renders nothing but a wait state removes
 * the race entirely.
 *
 * It is also why this path must be registered in Kinde's allowed callback URLs for EVERY
 * origin the app is served from — production, each Static Web Apps preview, and localhost.
 * An unregistered redirect URI fails at Kinde with a generic error that does not name the URL
 * it rejected.
 */
export function Callback() {
  const { isLoading, isAuthenticated } = useKindeAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;

    // Signed in: go where the user was headed. `state` round-trips the intended destination
    // through Kinde, so a deep link survives the sign-in detour.
    if (isAuthenticated) {
      const intended = sessionStorage.getItem('aware.returnTo');
      sessionStorage.removeItem('aware.returnTo');
      void navigate({ to: intended || homeRoute(), replace: true });
      return;
    }

    // Finished loading and still not authenticated — the exchange failed or the user backed
    // out. Send them to the front door rather than leaving them on a blank callback screen.
    void navigate({ to: '/signup', replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  return (
    <div className="callback" role="status" aria-live="polite">
      <p>Signing you in…</p>
    </div>
  );
}
